import uuid from 'uuid';
import { HttpClient } from '@packages/core/http';
import { ConfigService, Constants } from '@packages/core/config';
import { PushNotificationsService as AbstractPushNotificationsService } from '@packages/core/push-notifications';
import { getLogger } from '@packages/core/logging';
import { StorageService, WebStorageKeys } from '@packages/core/storage';
import * as firebase from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, deleteToken } from 'firebase/messaging';

const logger = getLogger('push-notifications');

/**
 * Blocked permission and browsers without the Notification API are expected, not failures.
 *
 * @param error The token or registration error.
 */
function logTokenError(error: unknown) {
    const expected =
        (error as { code?: string })?.code === 'messaging/permission-blocked' ||
        typeof window.Notification?.requestPermission !== 'function';

    if (expected) {
        logger.debug('Push notifications are blocked or unsupported', { error });
        return;
    }

    logger.error('Failed to get or register the push token', { error });
}

export class PushNotificationsService extends AbstractPushNotificationsService {
    protected deviceFingerprint = uuid();
    protected clientDeviceTypeId = 'WEB_BROWSER';

    private firebaseConfig: {
        apiKey?: string;
        projectId?: string;
        messagingSenderId?: string;
        appId?: string;
    };

    private didRegisterServiceWorker = false;

    constructor(storageService: StorageService, configService: ConfigService, http: HttpClient) {
        super(http);

        storageService.getItem(WebStorageKeys.BrowserDeviceId).then((deviceFingerprint) => {
            logger.debug('Resolved the device fingerprint', {
                deviceFingerprint: deviceFingerprint || this.deviceFingerprint,
            });

            if (!deviceFingerprint) {
                return storageService.setItem(WebStorageKeys.BrowserDeviceId, this.deviceFingerprint);
            }

            this.deviceFingerprint = deviceFingerprint;
        });

        const vapidKey = configService.get(Constants.Env.FirebaseVapidKey);
        this.firebaseConfig = {
            apiKey: configService.get(Constants.Env.FirebaseApiKey),
            projectId: configService.get(Constants.Env.FirebaseProjectId),
            messagingSenderId: configService.get(Constants.Env.FirebaseMessagingSenderId),
            appId: configService.get(Constants.Env.FirebaseAppId),
        };

        try {
            const firebaseApp = firebase.initializeApp(this.firebaseConfig);

            isSupported()
                .then((supported) => {
                    if (!supported) {
                        logger.debug('Firebase Messaging is unsupported in this browser');
                        return;
                    }

                    const messaging = getMessaging(firebaseApp);
                    logger.debug('Resolved the VAPID key from config', { hasVapidKey: Boolean(vapidKey) });
                    if (vapidKey) {
                        getToken(messaging, { vapidKey }).catch(logTokenError);
                    }
                })
                .catch((error) => {
                    logger.warn('Failed to check Firebase Messaging support', { error });
                });
        } catch (e) {
            logger.warn('Failed to initialize messaging', { error: e });
        }
    }

    registerServiceWorker() {
        logger.debug('Registering the messaging service worker');
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker
                // the source file lives in the `public/` folder; the path is absolute because a
                // relative one resolves against the page URL and 404s on deep links like /qmrs/...
                .register('/firebase-messaging-sw.js')
                .then(() => {
                    this.didRegisterServiceWorker = true;
                    logger.debug('Messaging service worker registered');
                })
                .catch((e) => {
                    logger.error('Failed to register the Firebase Messaging service worker', { error: e });
                });
        }
    }

    async init() {
        if (!this.didRegisterServiceWorker) {
            logger.warn('Failed to init push notifications - messaging is not available');
            return;
        }

        try {
            if (!(await isSupported())) {
                logger.warn('Failed to init push notifications - messaging is unsupported in this browser');
                return;
            }
        } catch (error) {
            logger.warn('Failed to check Firebase Messaging support', { error });
            return;
        }

        const messaging = getMessaging();

        try {
            // Even "data-only" messages _require_ permissions
            // unlike mobile, we can't get a token on Web without permissions.

            // await messaging.requestPermission();

            const getAndRegisterToken = async () => {
                try {
                    getToken(messaging)
                        .then((pushToken) => {
                            return this.registerDeviceToken(pushToken);
                        })
                        .catch(logTokenError);

                    // const pushToken = await getToken(messaging);
                    // await this.registerDeviceToken(pushToken);
                } catch (error) {
                    logger.error('Failed to get or register the push token', { error });
                }
            };

            // this.listenerTeardown.onTokenRefresh = messaging.onTokenRefresh(getAndRegisterToken);
            this.listenerTeardown.onLogout = () => {
                try {
                    deleteToken(messaging);
                } catch (error) {
                    if (error.code === 'messaging/token-unsubscribe-failed') {
                        logger.warn('Failed to clear the push token', { error });
                        return;
                    }

                    throw error;
                }
            };

            await getAndRegisterToken();

            // TODO: Message handling probably needs to move into
            // a UI/React hook on pages/components interested in
            // receiving & processing incoming messages.
            // This is only here for testing/debugging

            const foregroundOnMessageTeardown = onMessage(messaging, (m: any) => {
                logger.debug('Received a Firebase foreground message', { messageId: m?.messageId });
            });

            const onMessageDebug = (m: any) => {
                logger.debug('Received a background-posted message from the service worker', {
                    messageId: m?.data?.messageId,
                });
            };

            navigator.serviceWorker.addEventListener('message', onMessageDebug);
            this.listenerTeardown.onMessage = () => {
                foregroundOnMessageTeardown();
                navigator.serviceWorker.removeEventListener('message', onMessageDebug);
            };
        } catch (error) {
            if (
                error.code === 'messaging/permission-blocked' ||
                error.customData.serverStatus === 'PERMISSION_DENIED'
            ) {
                logger.warn('The notification permission was not granted and blocked instead', { error });
            } else {
                logger.error('Failed to start listening for notifications', { error });
            }
        }
    }
}
